import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { renderToString } from 'react-dom/server';
import { withI18n } from '@shopify/react-i18n';
import { Icon, Badge, Button, ButtonGroup, TextStyle } from '@shopify/polaris';
import { DuplicateMinor } from '@shopify/polaris-icons';
import { connect } from 'react-redux';
import beautify from 'simply-beautiful';

import { getShop } from 'src/helpers';
import { MAP_MODAL_TITLE, css } from 'src/constants/klaviyo';
import { pickImageNatural } from 'src/helpers/image-helper';
import { formatMoney } from 'src/helpers/money-format';
import { copyClipBoard, copyClipBoardNavigator } from 'src/helpers/shopify-integration-helpers';

import IconStar from 'src/assets/images/subscription-v4/star.svg?react';
import * as productAction from 'actions/product-actions';

interface ComposedPropsItem {
  title: string;
  description: string;
  isBadgeAI: boolean;
  type: String;
  recommendProducts: any;
  moneyFormat: string;
  onPreview: (type: string) => void;
  updateRecommendProducts: (recommendationType: string, products: any) => void;
}

const host = process.env.REACT_APP_ADMIN_V3_BASE_URL; // 'https://staging.bc-solutions.net'
const recommendUrl = `${host}/discovery/recommend`;

const RecommendationItem: React.FC<ComposedPropsItem> = ({
  title,
  description,
  isBadgeAI,
  type,
  recommendProducts,
  moneyFormat,
  onPreview,
  updateRecommendProducts,
  i18n
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const getPreviewHTML = products => {
    const renderRecommendProducts = [];
    if (!!products[type]?.length) {
      for (let i = 0; i < products[type].length; i += 2) {
        const itemFirst = products[type][i];
        const imageMainProductFirst = pickImageNatural(itemFirst.images);
        const priceFirst =
          Number(itemFirst.variants[0]?.price) || itemFirst.price_min || itemFirst.price_max;
        const compareAtPriceFirst =
          Number(itemFirst.variants[0]?.compare_at_price) ||
          itemFirst.compare_at_price_min ||
          itemFirst.compare_at_price_max;

        const itemSecond = products[type][i + 1];
        const imageMainProductSecond = pickImageNatural(itemSecond.images);
        const priceSecond =
          Number(itemSecond.variants[0]?.price) || itemSecond.price_min || itemSecond.price_max;
        const compareAtPriceSecond =
          Number(itemSecond.variants[0]?.compare_at_price) ||
          itemSecond.compare_at_price_min ||
          itemSecond.compare_at_price_max;

        renderRecommendProducts.push(
          <div style={{ margin: '0px auto', maxWidth: 600 }} key={i}>
            <table
              align="center"
              border={0}
              cellPadding={0}
              cellSpacing={0}
              role="presentation"
              style={{ width: '100%' }}>
              <tbody>
                <tr>
                  <td
                    style={{
                      direction: 'ltr',
                      fontSize: 0,
                      padding: '20px 0',
                      textAlign: 'center'
                    }}>
                    <div
                      className="mj-column-per-50 mj-outlook-group-fix"
                      style={{
                        fontSize: 0,
                        textAlign: 'left',
                        direction: 'ltr',
                        display: 'inline-block',
                        verticalAlign: 'top',
                        width: '100%'
                      }}>
                      <table
                        border={0}
                        cellPadding={0}
                        cellSpacing={0}
                        role="presentation"
                        style={{ verticalAlign: 'top' }}
                        width="100%">
                        <tbody>
                          <tr>
                            <td
                              align="center"
                              style={{
                                fontSize: 0,
                                padding: '10px 25px',
                                wordBreak: 'break-word'
                              }}>
                              <div
                                style={{
                                  fontSize: 13,
                                  lineHeight: 1,
                                  textAlign: 'center',
                                  color: '#000000'
                                }}>
                                <img
                                  src={imageMainProductFirst}
                                  style={{
                                    width: 160,
                                    height: 160,
                                    objectFit: 'contain',
                                    margin: 'auto',
                                    objectPosition: 'center'
                                  }}
                                  alt={itemFirst.title}
                                />
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td
                              align="center"
                              style={{
                                fontSize: 0,
                                padding: '10px 25px',
                                wordBreak: 'break-word'
                              }}>
                              <div
                                style={{
                                  fontSize: 16,
                                  fontWeight: 500,
                                  lineHeight: '20px',
                                  textAlign: 'center',
                                  color: 'rgb(32, 34, 35)'
                                }}>
                                {itemFirst.title}
                              </div>
                            </td>
                          </tr>
                          <tr>
                            <td
                              align="center"
                              style={{
                                fontSize: 0,
                                wordBreak: 'break-word'
                              }}>
                              <div
                                style={{
                                  fontSize: 14,
                                  fontWeight: 500,
                                  lineHeight: 1,
                                  textAlign: 'center',
                                  color: '#6d7175'
                                }}>
                                <span>{formatMoney(priceFirst, moneyFormat)}</span>

                                {!_.isNil(compareAtPriceFirst) &&
                                  compareAtPriceFirst > priceFirst && (
                                    <span
                                      style={{
                                        marginLeft: 6,
                                        textDecoration: 'line-through'
                                      }}>
                                      {formatMoney(compareAtPriceFirst, moneyFormat)}
                                    </span>
                                  )}
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    {itemSecond && (
                      <div
                        className="mj-column-per-50 mj-outlook-group-fix"
                        style={{
                          fontSize: 0,
                          textAlign: 'left',
                          direction: 'ltr',
                          display: 'inline-block',
                          verticalAlign: 'top',
                          width: '100%'
                        }}>
                        <table
                          border={0}
                          cellPadding={0}
                          cellSpacing={0}
                          role="presentation"
                          style={{ verticalAlign: 'top' }}
                          width="100%">
                          <tbody>
                            <tr>
                              <td
                                align="center"
                                style={{
                                  fontSize: 0,
                                  padding: '10px 25px',
                                  wordBreak: 'break-word'
                                }}>
                                <div
                                  style={{
                                    fontSize: 13,
                                    lineHeight: 1,
                                    textAlign: 'center',
                                    color: '#000000'
                                  }}>
                                  <img
                                    src={imageMainProductSecond}
                                    style={{
                                      width: 160,
                                      height: 160,
                                      objectFit: 'contain',
                                      margin: 'auto',
                                      objectPosition: 'center'
                                    }}
                                    alt={itemSecond.title}
                                  />
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td
                                align="center"
                                style={{
                                  fontSize: 0,
                                  padding: '10px 25px',
                                  wordBreak: 'break-word'
                                }}>
                                <div
                                  style={{
                                    fontSize: 16,
                                    fontWeight: 500,
                                    lineHeight: '20px',
                                    textAlign: 'center',
                                    color: 'rgb(32, 34, 35)'
                                  }}>
                                  {itemSecond.title}
                                </div>
                              </td>
                            </tr>
                            <tr>
                              <td
                                align="center"
                                style={{
                                  fontSize: 0,
                                  wordBreak: 'break-word'
                                }}>
                                <div
                                  style={{
                                    fontSize: 14,
                                    fontWeight: 500,
                                    lineHeight: 1,
                                    textAlign: 'center',
                                    color: '#6d7175'
                                  }}>
                                  <span>{formatMoney(priceSecond, moneyFormat)}</span>

                                  {!_.isNil(compareAtPriceSecond) &&
                                    compareAtPriceSecond > priceSecond && (
                                      <span
                                        style={{
                                          marginLeft: 6,
                                          textDecoration: 'line-through'
                                        }}>
                                        {formatMoney(compareAtPriceSecond, moneyFormat)}
                                      </span>
                                    )}
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        );
      }
    }

    return (
      <div className="preview-content">
        <p className="title-type">
          {MAP_MODAL_TITLE[type] ? String(i18n.translate(MAP_MODAL_TITLE[type])).toUpperCase() : ''}{' '}
          {String(
            i18n.translate('AdminHTML.KlaviyoIntegration.PreviewProductsModal.products')
          ).toUpperCase()}
        </p>

        <div className="product-container">{renderRecommendProducts}</div>
      </div>
    );
  };

  const htmlCode: string = useMemo(() => {
    return beautify.html(`${css}\n` + renderToString(getPreviewHTML(recommendProducts)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendProducts]);

  const copied = async (html: string) => {
    const toastMessage = i18n.translate('AdminHTML.KlaviyoIntegration.Common.copied');
    if (navigator) {
      await copyClipBoardNavigator(i18n, html, toastMessage);
    } else {
      copyClipBoard(i18n, html, toastMessage);
    }
  };

  const handleCopy = async () => {
    if (!_.isEmpty(_.get(recommendProducts, type))) {
      copied(htmlCode);
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(recommendUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json; charset=utf-8'
        },
        body: JSON.stringify({
          shop: getShop(),
          recommendationType: type,
          limit: 4,
          locale: 'en'
        })
      });
      if (response.status === 200) {
        const result = await response.json();
        const newProducts = _.cloneDeep(recommendProducts);
        _.set(newProducts, type, _.get(result, 'products', []));
        updateRecommendProducts(newProducts);

        const html = beautify.html(`${css}\n` + renderToString(getPreviewHTML(newProducts)));
        copied(html);
      }
    } catch (e) {
      console.log('ERROR', e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="recommendation-item">
      <div>
        <p className="recommendation-item__title">
          <TextStyle variation="strong">{i18n.translate(title)}</TextStyle>
          {isBadgeAI && (
            <Badge>
              <IconStar className="star" />
              <span className="text-ai">AI</span>
            </Badge>
          )}
        </p>
        <p>{i18n.translate(description)}</p>
      </div>
      <ButtonGroup spacing="tight">
        <Button
          loading={isLoading}
          onClick={handleCopy}
          icon={<Icon source={DuplicateMinor} />}></Button>
        <Button onClick={() => onPreview(type)}>
          <span style={{ fontWeight: 500 }}>
            {i18n.translate('AdminHTML.Common.Action.preview')}
          </span>
        </Button>
      </ButtonGroup>
    </div>
  );
};

const mapStateToProps = (state, props) => {
  const recommendProducts = _.get(state.product, 'recommendProducts', {});
  const moneyFormat = _.get(state.oauth.tenantApp, 'externalInfo.money_format', '');

  return {
    recommendProducts,
    moneyFormat
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateRecommendProducts: (recommendationType, products) =>
    dispatch(productAction.updateRecommendProducts(recommendationType, products))
});

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(RecommendationItem));
